import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

function DataTable({ 
  data, 
  columns, 
  loading, 
  renderRow,
  searchFields = [],
  emptyMessage = 'No data found',
  tableMinWidth,
  tableSx = {},
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Top and bottom horizontal scroll synchronization
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(1800);
  const isSyncingTop = useRef(false);
  const isSyncingBottom = useRef(false);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase();
    return data.filter((item) => {
      if (searchFields.length === 0) {
        return Object.values(item).some((value) => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
          }
          return false;
        });
      }
      
      return searchFields.some((field) => {
        const value = item[field];
        if (value && typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        return false;
      });
    });
  }, [data, searchTerm, searchFields]);

  // Paginate filtered data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleTopScroll = () => {
    if (isSyncingTop.current) {
      isSyncingTop.current = false;
      return;
    }
    if (topScrollRef.current && bottomScrollRef.current) {
      isSyncingBottom.current = true;
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (isSyncingBottom.current) {
      isSyncingBottom.current = false;
      return;
    }
    if (topScrollRef.current && bottomScrollRef.current) {
      isSyncingTop.current = true;
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  // Keep scrollbar width in sync with the table width
  useEffect(() => {
    const updateDimensions = () => {
      const tableW = tableRef.current?.offsetWidth || tableRef.current?.scrollWidth || 0;
      const containerW = bottomScrollRef.current?.scrollWidth || 0;
      const maxW = Math.max(tableW, containerW);
      if (maxW > 0) {
        setScrollWidth(maxW);
      }
    };

    updateDimensions();
    const t1 = setTimeout(updateDimensions, 100);
    const t2 = setTimeout(updateDimensions, 400);

    window.addEventListener('resize', updateDimensions);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && bottomScrollRef.current) {
      resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(bottomScrollRef.current);
      if (tableRef.current) {
        resizeObserver.observe(tableRef.current);
      }
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [data, columns, loading, paginatedData]);

  // High-visibility scrollbar styling for both top and bottom
  const scrollbarStyles = {
    overflowX: 'scroll',
    scrollbarWidth: 'auto',
    scrollbarColor: '#78909c #eceff1',
    '&::-webkit-scrollbar': {
      height: '14px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#eceff1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#78909c',
      borderRadius: '4px',
      border: '2px solid #eceff1',
      '&:hover': {
        backgroundColor: '#455a64',
      },
    },
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagination = (
    <TablePagination
      component="div"
      count={filteredData.length}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[5, 10, 25, 50]}
    />
  );

  return (
    <Box>
      {searchFields.length > 0 && (
        <TextField
          fullWidth
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 , width: '50%', display:"none" }}
        />
      )}

      {pagination}

      {/* Top horizontal scrollbar: Sticky at top so it is ALWAYS visible even when scrolling vertically */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 99,
          bgcolor: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderBottom: '2px solid #94a3b8',
          borderRadius: '6px',
          mb: 1,
          p: '4px 8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '2px',
            px: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem' }}>
            ◄ Horizontal Scroll Bar (Top) ►
          </Typography>
        </Box>
        <Box
          ref={topScrollRef}
          onScroll={handleTopScroll}
          sx={{
            ...scrollbarStyles,
            overflowY: 'hidden',
            height: '20px',
            width: '100%',
          }}
        >
          <div style={{ width: `${scrollWidth}px`, height: '20px', lineHeight: '20px' }}>
            &nbsp;
          </div>
        </Box>
      </Box>

      {/* Table Container with bottom horizontal scrollbar */}
      <TableContainer
        component={Paper}
        ref={bottomScrollRef}
        onScroll={handleBottomScroll}
        sx={{
          ...scrollbarStyles,
        }}
      >
        <Table ref={tableRef} sx={{ minWidth: tableMinWidth || 650, ...tableSx }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id || column.field}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    width: column.width,
                    maxWidth: column.maxWidth,
                    whiteSpace: column.whiteSpace || (column.minWidth ? 'nowrap' : 'inherit'),
                    fontWeight: 700,
                    ...column.sx,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => renderRow(row, index))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination}
    </Box>
  );
}

export default DataTable;
